/**
 * map-common.js
 */

import { point, lineString, nearestPointOnLine } from '@turf/turf';
import { uuidv4 } from 'utils/misc';


export const MAP_TYPE = {
  SATELLITE: 'hybrid',
  ROAD: 'roadmap',
  TERRAIN: 'terrain'
}


export const gmapCoords = (position) => {
  return { lat: position.latitude, lng: position.longitude };
}


// gets a point from coordinates snapped to given track's path
export const generateSnappedPoint = (track, lat, lng) => {
  // handle single point
  if (track.points.length === 1) {
    return {
      properties: {
        dist: 0.000001,
        location: 0
      },
      geometry: {
        coordinates: [track.points[0].longitude, track.points[0].latitude]
      }
    }
  }
  
  const pt = point([lng, lat]);
  const line = lineString(track.points.map((point) => [point.longitude, point.latitude]));
  return nearestPointOnLine(line, pt);
}


// const distanceOnTrack = (track, lat, lng) => {
//   return generateSnappedPoint(track, lat, lng).properties.location * 1000; // metres
// }


// generate a marker on the track at the closest point from the given coords
export const snappedMarkerFromPoint = (snappedPoint, user) => {
  if (snappedPoint && (snappedPoint.properties.dist * 1000) < 10) { // less than 10m away
    return {
      uuid: uuidv4(), // unique ID so we can find this regardless of persistence
      latitude: snappedPoint.geometry.coordinates[1],
      longitude: snappedPoint.geometry.coordinates[0],
      dateTimeCreated: new Date(),
      distanceOnTrack: snappedPoint.properties.location * 1000, // metres
      userId: user?.id,
      userName: user?.name,
    };
  } else {
    return null;
  }
}


// finds marker from track markers
export const getMarker = (track, markerUuid) => {
  return track.markers.find(m => m.uuid === markerUuid);
}


// Resize to max 2k px wide or high
export const processPhotoFile = (file) => {
  // TODO: abstract code for use elsewhere
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');

        const max_size = 2000;
        const quality = 0.6;  // WebP at 0.8 is comparable quality and smaller, but lacks DevExpress support
        let width = image.width;
        let height = image.height;
        if (width > height) {
          if (width > max_size) {
            height *= max_size / width;
            width = max_size;
          }
        } else {
          if (height > max_size) {
            width *= max_size / height;
            height = max_size;
          }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(image, 0, 0, width, height);

        // If we choose a different format to convert to we'll have to
        // replace the extension
        //const newFileName = file.name.replace(/\.[^/.]+$/, '.webp');

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: blob.type }));
        }, 'image/jpeg', quality);  // or e.g. 'image/webp'
      };
      image.src = reader.result;
    };

    reader.readAsDataURL(file);
  });
}


// Get, definitevely, or estimate, the time elapsed since the track start
// when a marker was passed. Note that "marker" can also be a selectedPoint,
// or any object with dateTimeCreated, latitude and longitude.
export const getMarkerTimeOnTrack = (track, marker) => {

  // If the marker was a "quick pin", the time the marker was created is the definitive
  // time on track.
  if (marker.isQuickPin) {
    const markerCreated = new Date(marker.dateTimeCreated);
    const trackStarted = new Date(track.dateTimeCreated);
    return {
      value: (markerCreated.getTime() - trackStarted.getTime()) / 1000,  // seconds
      isEstimate: false,
    };
  }

  // Otherwise, we estimate using linear interpolation based on the marker's position
  // const markerDistance = distanceOnTrack(track, marker.latitude, marker.longitude);
  const markerDistance = marker.distanceOnTrack;

  let prevPointDistance, nextPointDistance, prevPointElapsed, nextPointElapsed = 0;

  for (const point of track.points) {
    const pointDistance = point.distanceMeters;
    if (pointDistance >= markerDistance) {
      nextPointDistance = pointDistance;
      nextPointElapsed = point.elapsedSeconds;
      break;
    } else {
      prevPointDistance = pointDistance;
      prevPointElapsed = point.elapsedSeconds;
    }
  }

  if (nextPointDistance === 0) {
    // We never found a track point that was ahead of the marker, i.e. both must be at start
    return {
      value: 0,
      isEstimate: false,
    };
  }

  const proportion = (markerDistance - prevPointDistance) / (nextPointDistance - prevPointDistance);
  const elapsedTime = prevPointElapsed + (proportion * (nextPointElapsed - prevPointElapsed));

  return {
    value: Math.round(elapsedTime),
    isEstimate: true,
  };
}